// ConflictPanel.jsx — Errors (blocking) and warnings list.
import React, { useState } from "react";
import { AlertTriangle, AlertCircle, CheckCircle, ChevronDown, ChevronRight } from "lucide-react";

const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED   = "#E24B4A";

export default function ConflictPanel({ conflicts = [], onLeaveAction }) {
  const [open, setOpen] = useState(true);

  if (!conflicts || conflicts.length === 0) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: NEON, fontSize: 13 }}>
          <CheckCircle size={14} /> All clear — no conflicts detected.
        </div>
      </div>
    );
  }

  const errors   = conflicts.filter((c) => c.severity === "error");
  const warnings = conflicts.filter((c) => c.severity === "warning");

  const Item = ({ c }) => (
    <div style={{
      display: "flex", gap: 8, alignItems: "flex-start",
      padding: "8px 10px",
      borderRadius: 8,
      background: c.severity === "error" ? "rgba(226,75,74,0.06)" : "rgba(239,159,39,0.05)",
      border: `0.5px solid ${c.severity === "error" ? RED : AMBER}44`,
      marginBottom: 6,
    }}>
      <div style={{ color: c.severity === "error" ? RED : AMBER, paddingTop: 1 }}>
        {c.severity === "error" ? <AlertCircle size={13} /> : <AlertTriangle size={13} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "#ddd", lineHeight: 1.5 }}>{c.message}</div>
        {(c.user_name || c.date) && (
          <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
            {c.user_name && <span>{c.user_name}</span>}
            {c.user_name && c.date && <span> · </span>}
            {c.date && <span>{c.date}</span>}
            {c.shift_type && <span> · {c.shift_type}</span>}
          </div>
        )}
        {c.kind === "leave" && c.leave_id && onLeaveAction && (
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              onClick={() => onLeaveAction(c.leave_id, "approve")}
              style={btn("approve")}
            >Approve leave</button>
            <button
              onClick={() => onLeaveAction(c.leave_id, "reject")}
              style={btn("reject")}
            >Reject leave</button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={card}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "transparent", border: "none",
          color: "#ddd", fontSize: 13, padding: 0, cursor: "pointer",
          width: "100%", marginBottom: 10,
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Conflicts</span>
        <span style={{
          padding: "1px 8px", borderRadius: 999,
          background: errors.length ? "rgba(226,75,74,0.15)" : "rgba(239,159,39,0.12)",
          color: errors.length ? RED : AMBER, fontSize: 10,
        }}>
          {errors.length} error{errors.length === 1 ? "" : "s"} · {warnings.length} warning{warnings.length === 1 ? "" : "s"}
        </span>
      </button>

      {open && (
        <div>
          {errors.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={section}>Errors (blocking)</div>
              {errors.map((c, i) => <Item key={i} c={c} />)}
            </div>
          )}
          {warnings.length > 0 && (
            <div>
              <div style={section}>Warnings</div>
              {warnings.map((c, i) => <Item key={i} c={c} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const card = {
  background: "#0d0d0d",
  border: "0.5px solid #1c1c1c",
  borderRadius: 12,
  padding: 14,
  marginTop: 16,
};

const section = {
  fontSize: 10, color: "#777", textTransform: "uppercase",
  letterSpacing: 0.5, marginBottom: 6,
};

const btn = (kind) => ({
  padding: "3px 10px", fontSize: 10, borderRadius: 6,
  background: kind === "approve" ? "rgba(0,255,153,0.12)" : "rgba(226,75,74,0.12)",
  border: `0.5px solid ${kind === "approve" ? NEON : RED}55`,
  color: kind === "approve" ? NEON : RED,
  cursor: "pointer",
});
